import { useState } from 'react'
import {
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Typography,
  Chip,
  Box,
  Button,
  Rating,
  IconButton,
  Tooltip,
  Popover,
} from '@mui/material'
import { LocationOn, People, Star, Navigation, TrendingUp } from '@mui/icons-material'
import { Link as RouterLink } from 'react-router-dom'
import { AdventureDto } from '@api/types/adventure'
import { useGeolocation } from '@hooks/useGeolocation'
import { navigateTo } from '@utils/navigation'
import { useTags } from '@contexts/TagsContext'
import { getAdventureStatusLabel, getAdventureStatusColor } from '../types/adventure-editor.types'

interface AdventureCardProps {
  adventure: AdventureDto
}

export function AdventureCard({ adventure }: AdventureCardProps) {
  const { location: userLocation } = useGeolocation()
  const { getTags } = useTags()
  const [statsAnchorEl, setStatsAnchorEl] = useState<HTMLElement | null>(null)

  const handleStatsClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setStatsAnchorEl(event.currentTarget)
  }

  const handleStatsClose = () => {
    setStatsAnchorEl(null)
  }

  const hasStats = adventure.uniquePlayersLastHour !== undefined ||
    adventure.uniquePlayersLast24Hours !== undefined ||
    adventure.uniquePlayersLastMonth !== undefined ||
    adventure.uniquePlayersLastYear !== undefined

  const difficultyColors = {
    1: 'success',
    2: 'info',
    3: 'warning',
    4: 'warning',
    5: 'error',
  } as const

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* Stats Icon - Positioned top right */}
      {hasStats && (
        <IconButton
          size="small"
          onClick={handleStatsClick}
          title="View popularity stats"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            backgroundColor: 'background.paper',
            boxShadow: 1,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <TrendingUp fontSize="small" />
        </IconButton>
      )}
      <CardMedia
        component="img"
        height="200"
        image={adventure.thumbnailImageUrl || adventure.coverImageUrl || adventure.imageUrl || `https://picsum.photos/seed/${adventure.id}/400/200`}
        alt={adventure.title}
      />
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" gutterBottom noWrap>
          {adventure.title}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          component="div"
          sx={{
            mb: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            '& p': { margin: 0 },
            '& *': { margin: 0 },
          }}
          dangerouslySetInnerHTML={{ __html: adventure.description }}
        />

        <Box sx={{ mb: 2 }}>
          <Chip
            label={`Difficulty ${adventure.difficulty}/5`}
            size="small"
            color={difficultyColors[adventure.difficulty as keyof typeof difficultyColors] || 'default'}
            sx={{ mr: 1 }}
          />
          {adventure.status !== undefined && String(adventure.status) !== '3' && Number(adventure.status) !== 3 && (
            <Chip
              label={getAdventureStatusLabel(adventure.status)}
              size="small"
              color={getAdventureStatusColor(adventure.status)}
              variant="outlined"
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <People fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              {adventure.participantCount}
            </Typography>
          </Box>

          {adventure.averageRating && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Rating value={adventure.averageRating} size="small" readOnly precision={0.5} />
              <Typography variant="caption" color="text.secondary">
                ({adventure.completionCount})
              </Typography>
            </Box>
          )}
        </Box>

        {adventure.location && adventure.location.latitude && adventure.location.longitude && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
              <LocationOn fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary" noWrap>
                {adventure.location.address || `${adventure.location.latitude.toFixed(4)}, ${adventure.location.longitude.toFixed(4)}`}
              </Typography>
            </Box>
            <Tooltip title="Get Directions">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  navigateTo(adventure.location!, userLocation)
                }}
              >
                <Navigation fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {adventure.tags && adventure.tags.length > 0 && (
          <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {(() => {
              // Extract tag IDs - handle both string tags and object tags
              const tagIds = adventure.tags.map((tag: string | { id: string }) =>
                typeof tag === 'string' ? tag : tag.id
              )
              const tagObjects = getTags(tagIds)
              return (
                <>
                  {tagObjects.slice(0, 3).map((tag) => (
                    <Chip
                      key={tag.id}
                      label={tag.displayName || tag.name}
                      icon={tag.icon ? <span style={{ marginLeft: 4, fontSize: '0.875rem' }}>{tag.icon}</span> : undefined}
                      size="small"
                      variant="outlined"
                      sx={tag.color ? {
                        borderColor: tag.color,
                        color: tag.color,
                        '& .MuiChip-icon': { color: tag.color },
                      } : undefined}
                    />
                  ))}
                  {tagObjects.length > 3 && (
                    <Chip label={`+${tagObjects.length - 3}`} size="small" variant="outlined" />
                  )}
                </>
              )
            })()}
          </Box>
        )}
      </CardContent>

      <CardActions>
        <Button
          component={RouterLink}
          to={`/adventures/${adventure.id}`}
          size="small"
          fullWidth
          variant="contained"
        >
          View Details
        </Button>
      </CardActions>

      {/* Stats Popover */}
      <Popover
        open={Boolean(statsAnchorEl)}
        anchorEl={statsAnchorEl}
        onClose={handleStatsClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box sx={{ p: 2, minWidth: 220 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5 }}>
            Adventure Popularity
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Last Hour:</Typography>
              <Typography variant="body2" fontWeight={500}>
                {(adventure.uniquePlayersLastHour ?? 0).toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Last 24 Hours:</Typography>
              <Typography variant="body2" fontWeight={500}>
                {(adventure.uniquePlayersLast24Hours ?? 0).toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Last Month:</Typography>
              <Typography variant="body2" fontWeight={500}>
                {(adventure.uniquePlayersLastMonth ?? 0).toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Last Year:</Typography>
              <Typography variant="body2" fontWeight={500}>
                {(adventure.uniquePlayersLastYear ?? 0).toLocaleString()}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Popover>
    </Card>
  )
}
